/**
 * Night mode: swaps the paper/ink tokens (styles.css `.night`) for a dim
 * variant. Per-device (localStorage), applied before first paint by
 * `applyStoredNight()` in main.tsx so a 3am open never flashes white.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { setSuperProperty, track } from "./analytics.ts";

const STORAGE_KEY = "babia.night";
const THEME_COLOR = { day: "#f3f2f2", night: "#1e1e2e" } as const; // night: Catppuccin Mocha Base

function readStored(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY) === "1";
  } catch {
    return false;
  }
}

function apply(night: boolean) {
  document.documentElement.classList.toggle("night", night);
  document
    .querySelector('meta[name="theme-color"]')
    ?.setAttribute("content", night ? THEME_COLOR.night : THEME_COLOR.day);
}

/** Call once, synchronously, before React renders. */
export function applyStoredNight() {
  apply(readStored());
}

interface NightContextValue {
  night: boolean;
  toggle: () => void;
}

const NightContext = createContext<NightContextValue>({ night: false, toggle: () => {} });

export function NightProvider({ children }: { children: React.ReactNode }) {
  const [night, setNight] = useState(readStored);
  const previous = useRef(night);
  useEffect(() => {
    apply(night);
    // Every event says whether night mode was on (3am use); a switch is an event too.
    setSuperProperty("night_mode", night);
    if (previous.current !== night) track("night mode toggled", { night });
    previous.current = night;
    try {
      localStorage.setItem(STORAGE_KEY, night ? "1" : "0");
    } catch {
      // private mode / storage disabled: the toggle still works for this visit
    }
  }, [night]);
  const toggle = useCallback(() => setNight((n) => !n), []);
  const value = useMemo(() => ({ night, toggle }), [night, toggle]);
  return <NightContext.Provider value={value}>{children}</NightContext.Provider>;
}

export function useNight(): NightContextValue {
  return useContext(NightContext);
}
